import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";

/**
 * Standardized query keys for global search categories
 * This is the single source of truth for all search-related query keys
 * All components should import and use these keys for consistency
 */
export const SEARCH_QUERY_KEYS = {
  contacts: (uid) => ["contacts", uid],
  projects: () => ["projects"],
  todos: (uid) => ["todos", uid],
  receipts: (uid) => ["receipts", uid],
};

/**
 * Hook to invalidate search cache for specific categories
 * Use this after mutations to ensure the global search index stays in sync
 *
 * @example
 * const { invalidateCategory } = useSearchInvalidation();
 *
 * // After creating/updating/deleting a contact:
 * await createContactMutation.mutateAsync(data);
 * invalidateCategory("contacts");
 */
export function useSearchInvalidation() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  /**
   * Invalidate cache for a specific category
   * This triggers a refetch of the data, which updates the global search index
   */
  const invalidateCategory = useCallback(
    (category) => {
      const keyFn = SEARCH_QUERY_KEYS[category];
      if (!keyFn) {
        console.warn(`Unknown search category: ${category}`);
        return;
      }

      const key = category === "projects" ? keyFn() : keyFn(user?.uid);

      queryClient.invalidateQueries({ queryKey: key });
    },
    [queryClient, user?.uid]
  );

  /**
   * Invalidate cache for all search categories
   * Use sparingly - prefer invalidating specific categories when possible
   */
  const invalidateAll = useCallback(() => {
    Object.keys(SEARCH_QUERY_KEYS).forEach((category) => {
      invalidateCategory(category);
    });
  }, [invalidateCategory]);

  /**
   * Get the standardized query key for a category
   * Useful when you need the key for useQuery or setQueryData
   */
  const getQueryKey = useCallback(
    (category) => {
      const keyFn = SEARCH_QUERY_KEYS[category];
      if (!keyFn) {
        console.warn(`Unknown search category: ${category}`);
        return null;
      }
      return category === "projects" ? keyFn() : keyFn(user?.uid);
    },
    [user?.uid]
  );

  return {
    invalidateCategory,
    invalidateAll,
    getQueryKey,
    SEARCH_QUERY_KEYS,
  };
}

export default useSearchInvalidation;
